// src/components/MealPlanCard.ts
// Meal plan photo card with client-side image compression and R2 upload

import type { ShoppingList } from '../types';
import { api } from '../services/api';
import { lightbox } from './Lightbox';
import { showToast } from './Toast';

export class MealPlanCard {
  private el: HTMLElement;
  private fileInput: HTMLInputElement;
  private isExpanded = false;

  constructor(
    private list: ShoppingList,
    private isEditor: boolean,
    private onUpdated: () => void
  ) {
    this.el = document.createElement('div');
    this.el.className = 'meal-plan-card';

    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.accept = 'image/*';
    this.fileInput.style.display = 'none';
    this.fileInput.addEventListener('change', (e) => this.handleFileSelect(e));
    document.body.appendChild(this.fileInput);

    this.render();
  }

  public getElement(): HTMLElement {
    return this.el;
  }

  public update(list: ShoppingList, isEditor: boolean) {
    this.list = list;
    this.isEditor = isEditor;
    this.render();
  }

  private render() {
    const { mealPlanUrl, mealPlanUpdatedAt, archived } = this.list;
    const canEdit = this.isEditor && !archived;

    if (!mealPlanUrl && !canEdit) {
      this.el.style.display = 'none';
      return;
    }

    this.el.style.display = 'block';

    if (!mealPlanUrl) {
      // Empty state with upload button
      this.el.innerHTML = `
        <div class="meal-plan-header" style="justify-content:center;padding:1rem;cursor:pointer;" id="mp-upload-trigger">
          <div style="display:flex;align-items:center;gap:0.5rem;color:var(--sage);font-weight:600;font-size:0.9rem;">
            <span>📸</span>
            <span>Tap to add meal plan photo</span>
          </div>
        </div>
      `;
      this.el.querySelector('#mp-upload-trigger')?.addEventListener('click', () => {
        this.fileInput.click();
      });
      return;
    }

    // Has image
    const imgSrc = `${mealPlanUrl}?t=${mealPlanUpdatedAt || Date.now()}`;
    const actionsHtml = canEdit
      ? `
        <div class="meal-plan-actions">
          <button class="btn btn-secondary" id="mp-replace-btn" style="padding:0.3rem 0.7rem;font-size:0.8rem;">Replace</button>
          <button class="btn btn-danger" id="mp-delete-btn" style="padding:0.3rem 0.7rem;font-size:0.8rem;">Remove</button>
        </div>
      `
      : '';

    this.el.className = `meal-plan-card ${this.isExpanded ? 'expanded' : ''}`;
    this.el.innerHTML = `
      <div class="meal-plan-header" id="mp-header-toggle">
        <div class="meal-plan-header-title">
          <span>🍽️</span>
          <span>Meal Plan Photo</span>
        </div>
        <span class="meal-plan-chevron">▼</span>
      </div>
      <div class="meal-plan-content">
        <img class="meal-plan-img" src="${imgSrc}" alt="Meal Plan" id="mp-img-preview" />
        ${actionsHtml}
      </div>
    `;

    this.el.querySelector('#mp-header-toggle')?.addEventListener('click', () => {
      this.isExpanded = !this.isExpanded;
      this.render();
    });

    this.el.querySelector('#mp-img-preview')?.addEventListener('click', (e) => {
      e.stopPropagation();
      lightbox.open(imgSrc);
    });

    this.el.querySelector('#mp-replace-btn')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.fileInput.click();
    });

    this.el.querySelector('#mp-delete-btn')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (confirm('Remove meal plan photo?')) {
        try {
          await api.deleteMealPlan(this.list.id);
          showToast('Meal plan removed');
          this.onUpdated();
        } catch (err: any) {
          showToast('Error: ' + err.message);
        }
      }
    });
  }

  private async handleFileSelect(e: any) {
    const file = e.target.files?.[0];
    if (!file) return;
    this.fileInput.value = '';

    showToast('Compressing & uploading image…', 3500);

    try {
      const compressedBlob = await this.compressImage(file, 1200, 0.75);
      await api.uploadMealPlan(this.list.id, compressedBlob);
      this.isExpanded = true;
      showToast('Meal plan photo updated!');
      this.onUpdated();
    } catch (err: any) {
      showToast('Upload failed: ' + err.message);
    }
  }

  private compressImage(file: File, maxDim: number, quality: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        let w = img.width;
        let h = img.height;
        if (w > maxDim || h > maxDim) {
          const ratio = Math.min(maxDim / w, maxDim / h);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas context error'));
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('Compression failed'))),
          'image/jpeg',
          quality
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }
}
